// useAppFiles — owner-only READ-ONLY state for one app's source tree: the file
// listing grouped into folders, plus the text of whichever file is open.
//
// Same plain useState/useEffect + `cancelled` guard + nonce-refetch convention as
// useAppData / useAppSecrets (no react-query anywhere in this app). Two
// independent loads, because they fail independently: the LISTING can be there
// while one file refuses to read (it was deleted between the listing and the
// click), and the operator should see the tree with an error in the viewer
// rather than an error instead of the tree.

import { useCallback, useEffect, useMemo, useState } from "react";
import { listFiles, getSource, type AppFile } from "../api";
import { buildTree, isProbablyBinary, type TreeNode } from "../lib/file_tree";

export type OpenFile = {
  path:    string;
  /** null while the bytes are in flight. */
  content: string | null;
  error:   string | null;
  /** true ⇒ we did not fetch it: the extension says the bytes are not text. */
  binary:  boolean;
};

export type UseAppFiles = {
  tree:    TreeNode[] | null;      // null while loading
  files:   AppFile[] | null;       // the flat listing, for a count
  error:   string | null;
  /** The listing is a merge of two deployers; one being unreachable is reported,
   *  not hidden, because a partial tree looks exactly like a small app. */
  partial: string[];
  open:    OpenFile | null;
  reload:  () => void;
  openFile: (path: string) => void;
  close:    () => void;
};

export function useAppFiles(slug: string, env: "dev" | "prod" = "dev"): UseAppFiles {
  const [files,   setFiles]   = useState<AppFile[] | null>(null);
  const [error,   setError]   = useState<string | null>(null);
  const [partial, setPartial] = useState<string[]>([]);
  const [nonce,   setNonce]   = useState(0);

  const [openPath, setOpenPath] = useState<string | null>(null);
  const [content,  setContent]  = useState<string | null>(null);
  const [openErr,  setOpenErr]  = useState<string | null>(null);

  // The listing.
  useEffect(() => {
    let cancelled = false;
    setFiles(null); setError(null); setPartial([]);
    listFiles(slug, env)
      .then((r) => { if (!cancelled) { setFiles(r.files ?? []); setPartial(r.partial_errors ?? []); } })
      .catch((e) => { if (!cancelled) setError(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, [slug, env, nonce]);

  // The open file's bytes. Re-fetched on `nonce` too, so Refresh updates what is
  // on screen instead of leaving a stale pane next to a fresh tree.
  useEffect(() => {
    if (openPath === null) { setContent(null); setOpenErr(null); return; }
    if (isProbablyBinary(openPath)) { setContent(null); setOpenErr(null); return; }
    let cancelled = false;
    setContent(null); setOpenErr(null);
    getSource(slug, openPath, env)
      .then((r) => { if (!cancelled) setContent(typeof r?.content === "string" ? r.content : ""); })
      .catch((e) => { if (!cancelled) setOpenErr(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, [slug, env, openPath, nonce]);

  const tree = useMemo(() => (files === null ? null : buildTree(files)), [files]);

  const open: OpenFile | null = openPath === null ? null : {
    path:    openPath,
    content,
    error:   openErr,
    binary:  isProbablyBinary(openPath),
  };

  return {
    tree, files, error, partial, open,
    reload:   useCallback(() => setNonce((n) => n + 1), []),
    openFile: useCallback((path: string) => setOpenPath(path), []),
    close:    useCallback(() => setOpenPath(null), []),
  };
}
